import {
  BasketIcon,
  BuildingsIcon,
  BusIcon,
  CalendarIcon,
  CarIcon,
  CompassIcon,
  CopySimpleIcon,
  DatabaseIcon,
  LinkIcon,
  NoteBlankIcon,
  PaperPlaneTiltIcon,
  PathIcon,
  PencilSimpleIcon,
  SealCheckIcon,
  TagIcon,
  UserIcon,
  WalletIcon,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import BackButton from '@/components/blocks/shared/back-button';
import {
  ApproveGuard,
  DeleteGuard,
  EditGuard,
  ViewGuard,
} from '@/components/guards/permission-guards';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Container from '@/components/ui/container';
import DataTable from '@/components/ui/data-table';
import FilterSelect from '@/components/ui/filter-select';
import { NoOrdersPermission } from '@/components/ui/permission-fallback';
import Row from '@/components/ui/row';
import { showToast } from '@/components/ui/toast';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { DataTableColumn } from '@/types/datatable';
import { DetailedOrderData, DetailedOrderItem } from '@/types/orders';
import { formatCurrency } from '@/utils';
import { RESOURCES } from '@/utils/permissions';

import ProgressTracker from './progressTracker';

// Loading skeleton components
const SkeletonDetailItem = () => (
  <div className='flex items-start gap-3'>
    <div className='bg-gray-20 p-2 rounded-full mt-1 animate-pulse'>
      <div className='w-4 h-4 bg-gray-30 rounded'></div>
    </div>
    <div className='flex-1 min-w-0'>
      <div className='animate-pulse'>
        <div className='h-3 bg-gray-20 rounded w-1/3 mb-2'></div>
        <div className='h-4 bg-gray-20 rounded w-2/3'></div>
      </div>
    </div>
  </div>
);

const SkeletonStatusCard = () => (
  <Card className='p-6'>
    <div className='flex gap-5 items-center justify-between'>
      <div className='animate-pulse'>
        <div className='h-6 bg-gray-20 rounded w-32'></div>
      </div>
      <div className='animate-pulse'>
        <div className='h-10 bg-gray-20 rounded w-40'></div>
      </div>
    </div>
    <div className='mt-6 grid grid-cols-6 gap-4'>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className='animate-pulse'>
          <div className='h-2 bg-gray-20 rounded w-full'></div>
        </div>
      ))}
    </div>
  </Card>
);

const SkeletonTotalsCard = () => (
  <div className='bg-white border border-gray-20 rounded-lg overflow-hidden'>
    {Array.from({ length: 3 }).map((_, i) => (
      <div
        key={i}
        className='flex justify-between items-center px-4 py-5 border-b border-gray-20'
      >
        <div className='h-4 bg-gray-20 rounded w-24 animate-pulse'></div>
        <div className='h-4 bg-gray-20 rounded w-32 animate-pulse'></div>
      </div>
    ))}
    <div className='flex justify-between items-center px-4 py-5 bg-gray-5'>
      <div className='h-4 bg-gray-20 rounded w-24 animate-pulse'></div>
      <div className='h-4 bg-gray-20 rounded w-32 animate-pulse'></div>
    </div>
  </div>
);

const OrderShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useEnhancedApi();
  const { showSheet } = useGlobalState();

  const [order, setOrder] = useState<DetailedOrderData>();
  const [loading, setIsLoading] = useState(true);

  const [actionLoading, setActionLoading] = useState<
    'approved' | 'delete' | 'rejected' | null
  >(null);
  const fetchedOnceRef = useRef<string | null>(null);

  // fetch Order Data
  useEffect(() => {
    const fetchOrderData = async () => {
      if (!id) return;

      // Prevent duplicate initial calls (e.g., React StrictMode)
      if (fetchedOnceRef.current === id) return;
      fetchedOnceRef.current = id;

      setIsLoading(true);
      try {
        const orderResponse = await api.orders.getById(id);
        if (orderResponse.success) {
          setOrder(orderResponse.data.order);
        }
      } catch (error) {
        showToast('Error loading order data', false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrderData();
    // Intentionally only depend on id to avoid re-fetch loops from api identity changes
  }, [id]);

  const handleAction = async (
    actionType: 'approved' | 'delete' | 'rejected'
  ) => {
    if (!id) return;

    setActionLoading(actionType);
    try {
      let response;
      switch (actionType) {
        case 'approved':
          response = await api.orders.review(id, {
            approvalStatus: 'approved',
          });
          break;
        case 'rejected':
          response = await api.orders.review(id, {
            approvalStatus: 'rejected',
          });
          break;
        case 'delete':
          response = await api.orders.delete(id);
          break;
      }

      if (response?.success) {
        showToast(response.message, true);
        if (actionType === 'delete') {
          navigate('/orders');
        } else {
          window.location.reload();
        }
      } else {
        showToast(response?.message || 'Action failed', false);
      }
    } catch (error) {
      showToast('Error performing action', false);
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangeStatus = async (selectedValue: string) => {
    const response = await api.orders.updateStatus(id!, {
      status: selectedValue,
    });
    if (response?.message) {
      showToast(response.message, true);
      window.location.reload();
    } else {
      showToast(response?.message, false);
    }
  };

  const handlePaymentStatus = async (selectedValue: string) => {
    const response = await api.orders.updateStatus(id!, {
      paymentStatus: selectedValue,
    });
    if (response?.message) {
      showToast(response.message, true);
      window.location.reload();
    } else {
      showToast(response?.message, false);
    }
  };

  const steps = [
    { label: 'Received', name: 'received', icon: <BasketIcon size={32} /> },
    { label: 'Processing', name: 'processing', icon: <PathIcon size={32} /> },
    {
      label: 'Awaiting Pickup',
      name: 'awaitingPickup',
      icon: <PaperPlaneTiltIcon size={32} />,
    },
    {
      label: 'Rider Assigned',
      name: 'riderAssigned',
      icon: <CompassIcon size={32} />,
    },
    {
      label: 'Dispatched',
      name: 'outForDelivery',
      icon: <CarIcon size={32} />,
    },
    {
      label: 'Delivered',
      name: 'delivered',
      icon: <SealCheckIcon size={32} />,
    },
  ];

  const ItemColumns: DataTableColumn<DetailedOrderItem>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessor: 'name',
        cell: ({ row }) => {
          const item = row.original;

          // If item has variants, render each variant
          if (item.variants && item.variants.length > 0) {
            return (
              <div className='space-y-1'>
                {item.variants.map((variant, index) => (
                  <div key={variant.variantId || index} className='text-sm'>
                    {variant.name}
                  </div>
                ))}
              </div>
            );
          }

          // Otherwise render the item name
          return <span className=''>{item.name}</span>;
        },
      },
      {
        id: 'quantity',
        header: 'Quantity',
        accessor: 'quantity',
        cell: ({ row }) => {
          const item = row.original;

          // If item has variants, render each variant's quantity
          if (item.variants && item.variants.length > 0) {
            return (
              <div className='space-y-1'>
                {item.variants.map((variant, index) => (
                  <div key={variant.variantId || index} className='text-sm'>
                    {variant.quantity}
                  </div>
                ))}
              </div>
            );
          }

          // Otherwise render the item quantity
          return <span className=''>{item.quantity}</span>;
        },
      },
      {
        id: 'price',
        header: 'Unit Price',
        accessor: 'unitPrice',
        cell: ({ row }) => {
          const item = row.original;

          // If item has variants, render each variant's unit price
          if (item.variants && item.variants.length > 0) {
            return (
              <div className='space-y-1'>
                {item.variants.map((variant, index) => (
                  <div key={variant.variantId || index} className='text-sm'>
                    {formatCurrency(Number(variant.unitPrice), 'NGN')}
                  </div>
                ))}
              </div>
            );
          }

          // Otherwise render the item unit price
          return (
            <span className=''>
              {formatCurrency(Number(item.unitPrice), 'NGN')}
            </span>
          );
        },
      },
      {
        id: 'subTotal',
        header: 'Sub Total',
        accessor: 'subTotal',
        cell: ({ row }) => {
          const item = row.original;

          // If item has variants, render each variant's subtotal
          if (item.variants && item.variants.length > 0) {
            return (
              <div className='space-y-1'>
                {item.variants.map((variant, index) => (
                  <div key={variant.variantId || index} className='text-sm'>
                    {formatCurrency(Number(variant.subtotal), 'NGN')}
                  </div>
                ))}
              </div>
            );
          }

          // Otherwise render the item subtotal
          return (
            <span className=''>
              {formatCurrency(Number(item.subTotal), 'NGN')}
            </span>
          );
        },
      },
    ],
    []
  );

  const editOrder = () => {
    showSheet({
      id: 'order-update',
      type: 'order-update',
      title: 'Edit Order',
      data: {
        order,
        onOrderUpdated: async () => {
          if (id) {
            const response = await api.orders.getById(id);
            if (response.success) {
              setOrder(response.data?.order);
            }
          }
        },
      },
    });
  };

  const calculateSubTotal = () => {
    if (!order?.Items || !Array.isArray(order.Items)) return 0;
    return order.Items.reduce((total, item) => total + (item.subTotal || 0), 0);
  };

  const subTotal = calculateSubTotal();
  const discount = order?.discountAmount || 0;
  const deliveryFee = order?.delivery?.deliveryFee || 0;
  const total = subTotal - discount + deliveryFee;

  if (loading) {
    return (
      <>
        <Container className='space-y-6'>
          <Row>
            <div className='flex items-center justify-between gap-6'>
              <div className='flex items-center gap-4'>
                <div className='animate-pulse'>
                  <div className='h-10 w-10 bg-gray-20 rounded'></div>
                </div>
                <div className='animate-pulse'>
                  <div className='h-8 bg-gray-20 rounded w-48 mb-2'></div>
                  <div className='h-4 bg-gray-20 rounded w-32'></div>
                </div>
              </div>
              <div className='flex items-center gap-2'>
                <div className='animate-pulse'>
                  <div className='h-10 w-20 bg-gray-20 rounded'></div>
                </div>
                <div className='animate-pulse'>
                  <div className='h-10 w-24 bg-gray-20 rounded'></div>
                </div>
              </div>
            </div>
          </Row>
        </Container>

        <div className='grid grid-cols-3 gap-6 mt-5 mx-10 mb-20'>
          <div className='col-span-2 space-y-5'>
            <SkeletonStatusCard />
            <Card className='p-6'>
              <div className='space-y-4'>
                <div className='animate-pulse h-4 bg-gray-20 w-32 rounded'></div>
                <div className='border rounded-lg'>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className='p-4 border-b last:border-b-0'>
                      <div className='grid grid-cols-4 gap-4'>
                        {Array.from({ length: 4 }).map((_, j) => (
                          <div key={j} className='animate-pulse'>
                            <div className='h-4 bg-gray-20 rounded'></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
            <SkeletonTotalsCard />
          </div>
          <div className='col-span-1'>
            <Card className='p-6'>
              <div className='animate-pulse mb-6'>
                <div className='h-6 bg-gray-20 rounded w-48'></div>
              </div>
              <div className='space-y-6'>
                {Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonDetailItem key={i} />
                ))}
              </div>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <ViewGuard
      resource={RESOURCES.SALES_ORDERS}
      fallback={<NoOrdersPermission onRequestAccess={() => {}} />}
    >
      <Container>
        <Row>
          <div className='flex items-center justify-between gap-6'>
            <BackButton
              text='Back to Orders'
              title={order?.orderId}
              to='/orders'
            />

            <div className='flex items-center gap-3'>
              {order?.status !== 'delivered' && (
                <EditGuard resource={RESOURCES.SALES_ORDERS}>
                  <Button
                    color='gray'
                    hierarchy='outlined'
                    className='flex items-center gap-2 cursor-pointer'
                    onClick={editOrder}
                    disabled={loading}
                  >
                    <PencilSimpleIcon size={16} />
                    Edit
                  </Button>
                </EditGuard>
              )}

              {order?.approvalStatus === 'pending' && (
                <>
                  <ApproveGuard resource={RESOURCES.SALES_ORDERS}>
                    <Button
                      color='destructive'
                      hierarchy='primary'
                      onClick={() => handleAction('rejected')}
                      className='flex items-center gap-2 cursor-pointer'
                      disabled={actionLoading !== null || loading}
                    >
                      {actionLoading === 'rejected' ? (
                        <div className='animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full' />
                      ) : null}
                      Decline
                    </Button>
                  </ApproveGuard>
                  <ApproveGuard resource={RESOURCES.SALES_ORDERS}>
                    <Button
                      color='brand'
                      hierarchy='primary'
                      onClick={() => handleAction('approved')}
                      className='flex items-center gap-2 cursor-pointer'
                      disabled={actionLoading !== null || loading}
                    >
                      {actionLoading === 'approved' ? (
                        <div className='animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full' />
                      ) : null}
                      Approve
                    </Button>
                  </ApproveGuard>
                </>
              )}
              {order?.approvalStatus === 'approved' &&
                order?.status !== 'delivered' && (
                  <DeleteGuard resource={RESOURCES.SALES_ORDERS}>
                    <Button
                      color='destructive'
                      hierarchy='primary'
                      onClick={() => handleAction('delete')}
                      className='flex items-center gap-2 cursor-pointer'
                      disabled={actionLoading !== null || loading}
                    >
                      {actionLoading === 'delete' ? (
                        <div className='animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full' />
                      ) : null}
                      Delete Order
                    </Button>
                  </DeleteGuard>
                )}
              {order?.approvalStatus === 'rejected' && (
                <DeleteGuard resource={RESOURCES.SALES_ORDERS}>
                  <Button
                    color='destructive'
                    hierarchy='primary'
                    onClick={() => handleAction('delete')}
                    className='flex items-center gap-2 cursor-pointer'
                    disabled={actionLoading !== null || loading}
                  >
                    {actionLoading === 'delete' ? (
                      <div className='animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full' />
                    ) : null}
                    Delete Order
                  </Button>
                </DeleteGuard>
              )}
            </div>
          </div>
        </Row>
      </Container>

      <div className='grid grid-cols-3 gap-6 mt-5 mx-10 mb-20'>
        <div className='col-span-2'>
          {order?.approvalStatus !== 'pending' &&
            order?.approvalStatus !== 'rejected' && (
              <div className='border rounded-2xl bg-white shadow-2xl mb-5 p-5'>
                <div className='flex gap-5 items-center'>
                  <h2 className='font-bold'>Order status</h2>
                  <FilterSelect
                    options={[
                      { value: 'received', label: 'Received' },
                      { value: 'processing', label: 'Processing' },
                      { value: 'awaitingPickup', label: 'Awaiting Pickup' },
                      { value: 'riderAssigned', label: 'Rider Assigned' },
                      { value: 'outForDelivery', label: 'Dispatched' },
                      { value: 'delivered', label: 'Delivered' },
                    ]}
                    className='mt-2 md:mt-0  md:w-[20%]'
                    icon='filterIcon'
                    onChange={handleChangeStatus}
                    value={order?.status}
                    disabled={
                      order?.status === 'delivered' ||
                      loading ||
                      actionLoading !== null
                    }
                  />
                </div>
                <ProgressTracker
                  steps={steps}
                  currentStep={order?.status || ''}
                />
              </div>
            )}

          <Row>
            <DataTable
              data={order?.Items || []}
              columns={ItemColumns}
              loading={loading}
              header={{
                title: 'Items Details',
                showSearch: false,
                enableFilter: false,
              }}
              emptyConfig={{
                title: 'No Items Found',
              }}
              enableSorting={false}
              enablePagination={false}
              enableRecordCount={false}
            />
          </Row>
          <div className=' bg-white border border-gray-20 rounded-lg overflow-hidden'>
            <div className='flex justify-between items-center px-4 py-5 border-b border-gray-20'>
              <span className='text-gray-60 text-sm'>SubTotal</span>
              <span className='text-gray-80 text-sm font-medium'>
                {formatCurrency(subTotal, 'NGN')}
              </span>
            </div>

            <div className='flex justify-between items-center px-4 py-5 border-b border-gray-20'>
              <span className='text-gray-60 text-sm'>Discount</span>
              <span className='text-gray-80 text-sm font-medium'>
                {formatCurrency(discount, 'NGN')}
              </span>
            </div>

            <div className='flex justify-between items-center px-4 py-5 border-b border-gray-20'>
              <span className='text-gray-60 text-sm'>Delivery Fee</span>

              <span className='text-gray-80 text-sm font-medium'>
                {formatCurrency(deliveryFee, 'NGN')}
              </span>
            </div>

            <div className='flex justify-between items-center px-4 py-5 bg-gray-5'>
              <span className='text-gray-80 text-sm font-semibold'>Total</span>
              <span className='text-gray-80 text-sm font-bold'>
                {formatCurrency(total, 'NGN')}
              </span>
            </div>
          </div>
        </div>
        <div className='col-span-1'>
          <Card className='px-5'>
            <h2 className='text-xl font-semibold mb-6'>
              Sales Order Information
            </h2>

            <div className='space-y-6'>
              <div className='flex items-center gap-3'>
                <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                  <TagIcon size={32} />
                </div>
                <div>
                  <p className='text-sm text-gray-60 mb-1 font-bold'>
                    Order ID
                  </p>
                  <p className='text-gray-60 text-sm'>
                    {order?.orderId || 'N/A'}
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-3'>
                <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                  <CalendarIcon size={32} />
                </div>
                <div>
                  <p className='text-sm text-gray-60 mb-1 font-bold'>
                    Order Date
                  </p>
                  <p className='text-gray-60 text-sm'>
                    {order?.createdAt
                      ? new Date(order.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-3'>
                <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                  <UserIcon size={32} />
                </div>
                <div>
                  <p className='text-sm text-gray-60 mb-1 font-bold'>
                    Created By
                  </p>
                  <p className='text-gray-60 text-sm'>
                    {order?.createdBy.username || 'N/A'}
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-3'>
                <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                  <DatabaseIcon size={32} />
                </div>
                <div>
                  <p className='text-sm text-gray-60 mb-1 font-bold'>Source</p>
                  <p className='text-gray-60 text-sm'>
                    {order?.source || 'N/A'}
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-3'>
                <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                  <PathIcon size={32} />
                </div>
                <div>
                  <p className='text-sm text-gray-60 mb-1 font-bold'>
                    Order Status
                  </p>
                  <p className='text-gray-60 text-sm'>
                    {order?.approvalStatus || 'N/A'}
                  </p>
                </div>
              </div>
              <div className='flex justify-between items-center gap-3'>
                <div className='flex items-center gap-3'>
                  <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                    <UserIcon size={32} />
                  </div>
                  <div>
                    <p className='text-sm text-gray-60 mb-1 font-bold'>
                      Business Name
                    </p>
                    <p className='text-gray-60 text-sm'>
                      {order?.customer.contactName || 'N/A'}
                    </p>
                  </div>
                </div>

                <div className='bg-brand-5 px-5 py-1 font-bold text-sm capitalize rounded-full text-brand-60 cursor-pointer'>
                  view
                </div>
              </div>

              <div className='flex justify-between items-center gap-3'>
                <div className='flex items-center gap-3'>
                  <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                    <WalletIcon size={32} />
                  </div>
                  <div>
                    <p className='text-sm text-gray-60 mb-1 font-bold'>
                      Payment Status
                    </p>
                    <p className='text-gray-60 text-sm'>
                      {order?.paymentStatus || 'N/A'}
                    </p>
                  </div>
                </div>

                <FilterSelect
                  options={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'paid', label: 'Paid' },
                    { value: 'unpaid', label: 'Unpaid' },
                  ]}
                  className='mt-2 md:mt-0  md:w-[50%]'
                  icon='filterIcon'
                  onChange={handlePaymentStatus}
                  value={order?.paymentStatus}
                  disabled={
                    order?.paymentStatus === 'paid' ||
                    loading ||
                    actionLoading !== null
                  }
                />
              </div>

              {order?.paymentLink && (
                <div className='flex justify-between items-center gap-3'>
                  <div className='flex items-center gap-3'>
                    <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                      <LinkIcon size={32} />
                    </div>
                    <div>
                      <p className='text-sm text-gray-60 mb-1 font-bold'>
                        Payment Link
                      </p>
                      <p className='text-gray-60 text-sm truncate max-w-[150px]'>
                        {order.paymentLink}
                      </p>
                    </div>
                  </div>

                  <button
                    className='cursor-pointer'
                    onClick={() => {
                      navigator.clipboard.writeText(order.paymentLink!);
                      showToast('Payment link copied to clipboard', true);
                    }}
                  >
                    <CopySimpleIcon size={24} />
                  </button>
                </div>
              )}

              <div className='flex items-center gap-3'>
                <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                  <SealCheckIcon size={32} />
                </div>
                <div>
                  <p className='text-sm text-gray-60 mb-1 font-bold'>Status</p>
                  <p className='text-gray-60 text-sm capitalize'>
                    {order?.status || 'N/A'}
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-3'>
                <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                  <BusIcon size={32} />
                </div>
                <div>
                  <p className='text-sm text-gray-60 mb-1 font-bold'>
                    Delivery Method
                  </p>
                  <p className='text-gray-60 text-sm capitalize'>
                    {order?.delivery?.deliveryMethod || 'N/A'}
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-3'>
                <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                  <UserIcon size={32} />
                </div>
                <div>
                  <p className='text-sm text-gray-60 mb-1 font-bold'>
                    Rider Name
                  </p>
                  <p className='text-gray-60 text-sm capitalize'>
                    {order?.delivery?.riderName || 'Not Assigned'}
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-3'>
                <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                  <BuildingsIcon size={32} />
                </div>
                <div>
                  <p className='text-sm text-gray-60 mb-1 font-bold'>
                    Delivery Address
                  </p>
                  <p className='text-gray-60 text-sm capitalize'>
                    {order?.delivery?.deliveryAddress || 'Not Assigned'}
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-3'>
                <div className='bg-brand-5 p-2 rounded-full mt-1 text-brand-60'>
                  <NoteBlankIcon size={32} />
                </div>
                <div>
                  <p className='text-sm text-gray-60 mb-1 font-bold'>
                    Delivery Note
                  </p>
                  <p className='text-gray-60 text-sm capitalize'>
                    {order?.delivery?.deliveryNote || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </ViewGuard>
  );
};

export default OrderShow;
